/**
 * Download public screen catalog Excel for selected gids.
 * Reuses POST /screens/export (same as PublicScreenCatalog).
 *
 * @param {string[]} gids
 * @param {{ maxExport?: number, campaignRtbId?: number }} [options]
 * @returns {Promise<{ ok: true, filename: string } | { ok: false, error: string }>}
 */
export async function exportScreensExcel(gids, options = {}) {
    const maxExport = options.maxExport ?? 200;
    const list = Array.isArray(gids) ? gids.map(String).filter(Boolean) : [];

    if (list.length === 0) {
        return { ok: false, error: 'Выберите хотя бы один экран' };
    }

    if (list.length > maxExport) {
        return {
            ok: false,
            error: `Можно выгрузить не более ${maxExport} экранов за раз (выбрано ${list.length})`,
        };
    }

    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

    const response = await fetch('/screens/export', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': csrf,
        },
        body: JSON.stringify({
            gids: list,
            ...(options.campaignRtbId ? { campaign_rtb_id: options.campaignRtbId } : {}),
        }),
    });

    if (response.status === 429) {
        return { ok: false, error: 'Слишком много запросов. Подождите немного и попробуйте снова.' };
    }

    if (!response.ok) {
        let message = `Не удалось выгрузить экраны (HTTP ${response.status})`;
        try {
            const data = await response.json();
            message = data.message || Object.values(data.errors || {}).flat().join(' ') || message;
        } catch (_) {
            // ignore non-json error bodies
        }
        return { ok: false, error: message };
    }

    const blob = await response.blob();
    const filename = extractDownloadFilename(response) || `2digital-screens-${new Date().toISOString().slice(0, 10)}.xlsx`;
    triggerBlobDownload(blob, filename);

    return { ok: true, filename };
}

export function extractDownloadFilename(response) {
    const header = typeof response?.headers?.get === 'function'
        ? (response.headers.get('Content-Disposition') || '')
        : (response?.headers?.['content-disposition']
            || response?.headers?.['Content-Disposition']
            || '');

    const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match) {
        return decodeURIComponent(utf8Match[1]);
    }

    const asciiMatch = header.match(/filename="?([^";]+)"?/i);
    return asciiMatch ? asciiMatch[1] : null;
}

export function triggerBlobDownload(blob, filename) {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
}
